import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { getStoredAuthToken } from '../../auth/auth-token';

export interface VideoEntity {
  id?: string;
  _id?: string;
  title?: string;
  description?: string;
  category?: string;
  video_url?: string;
  cloudinary_public_id?: string;
  thumbnail_url?: string;
  duration_seconds?: number;
  status?: string;
  linked_movie_id?: number | null;
  published_at?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

export interface VideoListResponse {
  total?: number;
  page?: number;
  per_page?: number;
  pages?: number;
  results?: VideoEntity[];
  [key: string]: unknown;
}

export interface VideoListParams {
  page?: number;
  per_page?: number;
  status?: string;
  category?: string;
  search?: string;
  linked?: boolean;
  sort_by?: string;
  [key: string]: unknown;
}

export interface CloudinarySignature {
  cloud_name: string;
  api_key: string;
  timestamp: number;
  signature: string;
  folder: string;
  upload_url: string;
}

@Injectable({ providedIn: 'root' })
export class VideoApiService {
  private readonly publicBase = `${environment.backendUrl}/api/videos`;
  private readonly adminBase = `${environment.backendUrl}/api/admin/videos`;
  private readonly signatureEndpoint = `${environment.backendUrl}/api/videos/cloudinary-signature`;
  private readonly uploadEndpoint = `${environment.backendUrl}/api/videos/upload-video`;

  constructor(
    private readonly http: HttpClient,
    @Inject(PLATFORM_ID) private readonly platformId: object,
  ) {}

  // ── Public ─────────────────────────────────────────────────────────────

  listPublishedVideos(params: VideoListParams): Observable<VideoListResponse> {
    return this.http.get<VideoListResponse>(this.publicBase, { params: this.toHttpParams(params) });
  }

  getPublishedVideo(videoId: string): Observable<VideoEntity> {
    return this.http.get<VideoEntity>(`${this.publicBase}/${encodeURIComponent(videoId)}`);
  }

  // ── Admin ──────────────────────────────────────────────────────────────

  adminListVideos(params: VideoListParams, token?: string): Observable<VideoListResponse> {
    return this.http.get<VideoListResponse>(this.adminBase, {
      params: this.toHttpParams(params),
      headers: this.authHeaders(token),
    });
  }

  adminGetStats(token?: string): Observable<Record<string, unknown>> {
    return this.http.get<Record<string, unknown>>(`${this.adminBase}/stats`, {
      headers: this.authHeaders(token),
    });
  }

  adminGetVideo(videoId: string, token?: string): Observable<VideoEntity> {
    return this.http.get<VideoEntity>(`${this.adminBase}/${encodeURIComponent(videoId)}`, {
      headers: this.authHeaders(token),
    });
  }

  adminCreateVideo(payload: Record<string, unknown>, token?: string): Observable<VideoEntity> {
    return this.http.post<VideoEntity>(this.adminBase, payload, { headers: this.authHeaders(token) });
  }

  adminUpdateVideo(videoId: string, payload: Record<string, unknown>, token?: string): Observable<VideoEntity> {
    return this.http.patch<VideoEntity>(`${this.adminBase}/${encodeURIComponent(videoId)}`, payload, {
      headers: this.authHeaders(token),
    });
  }

  adminDeleteVideo(videoId: string, token?: string): Observable<Record<string, unknown>> {
    return this.http.delete<Record<string, unknown>>(`${this.adminBase}/${encodeURIComponent(videoId)}`, {
      headers: this.authHeaders(token),
    });
  }

  adminPublishVideo(videoId: string, token?: string): Observable<Record<string, unknown>> {
    return this.http.patch<Record<string, unknown>>(
      `${this.adminBase}/${encodeURIComponent(videoId)}/publish`,
      {},
      { headers: this.authHeaders(token) },
    );
  }

  adminArchiveVideo(videoId: string, token?: string): Observable<Record<string, unknown>> {
    return this.http.patch<Record<string, unknown>>(
      `${this.adminBase}/${encodeURIComponent(videoId)}/archive`,
      {},
      { headers: this.authHeaders(token) },
    );
  }

  // ── Cloudinary ─────────────────────────────────────────────────────────

  getCloudinarySignature(token?: string): Observable<CloudinarySignature> {
    return this.http.get<CloudinarySignature>(this.signatureEndpoint, {
      headers: this.authHeaders(token),
    });
  }

  uploadVideoServer(file: File, token?: string): Observable<Record<string, unknown>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<Record<string, unknown>>(this.uploadEndpoint, formData, {
      headers: this.authHeaders(token),
    });
  }

  // ── Helpers ────────────────────────────────────────────────────────────

  private toHttpParams(params: Record<string, unknown>): HttpParams {
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      httpParams = httpParams.set(key, String(value));
    });
    return httpParams;
  }

  private authHeaders(explicitToken?: string): HttpHeaders {
    const token = explicitToken?.trim() || this.getSessionToken();
    if (!token) return new HttpHeaders();
    return new HttpHeaders({
      Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}`,
    });
  }

  private getSessionToken(): string {
    if (!isPlatformBrowser(this.platformId)) return '';
    return getStoredAuthToken();
  }
}
